/*
 * Automatic collection loading for Dawn.
 * Uses a throttled scroll/resize check rather than relying on an IntersectionObserver
 * callback. Each facet rerender creates a fresh element and starts its own listener.
 */
if (!customElements.get('collection-infinite-scroll')) {
  customElements.define('collection-infinite-scroll', class extends HTMLElement {
    connectedCallback() {
      this.scheduled = false;
      this.loading = false;
      this.finished = false;
      this.onScroll = () => {
        if (this.scheduled || this.loading || this.finished) return;
        this.scheduled = true;
        requestAnimationFrame(() => {
          this.scheduled = false;
          this.checkPosition();
        });
      };
      this.onRetry = () => {
        this.error.hidden = true;
        this.fallback.hidden = true;
        this.loadNext();
      };
      // Also works when the section was inserted by Dawn's facet filtering.
      requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected) return;
      this.grid = this.closest('#ProductGridContainer')?.querySelector('#product-grid');
      this.loader = this.querySelector('.barveze-infinite-scroll__loader');
      this.error = this.querySelector('.barveze-infinite-scroll__error');
      this.fallback = this.querySelector('.barveze-infinite-scroll__fallback');
      this.retry = this.querySelector('.barveze-infinite-scroll__error button');
      this.nextUrl = this.dataset.nextUrl || '';

      if (!this.grid || !this.loader || !this.error || !this.fallback || !this.nextUrl) {
        this.fallback && (this.fallback.hidden = false);
        return;
      }

      this.retry?.addEventListener('click', this.onRetry);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.classList.add('is-ready');
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.retry?.removeEventListener('click', this.onRetry);
      this.abortController?.abort();
    }

    checkPosition() {
      if (!this.isConnected || this.loading || this.finished) return;
      if (this.getBoundingClientRect().top < window.innerHeight + 500) this.loadNext();
    }

    async fetchPage(url) {
      // Section Rendering API keeps the payload small and preserves page/sort/filter parameters.
      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
      const first = await fetch(sectionUrl.href, {
        signal: this.abortController.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!first.ok) throw new Error('Section response ' + first.status);
      let html = await first.text();
      let documentPage = new DOMParser().parseFromString(html, 'text/html');
      if (documentPage.querySelector('#product-grid')) return documentPage;

      // Some themes do not return the grid from Section Rendering; retry the regular page.
      const second = await fetch(url.href, {
        signal: this.abortController.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });
      if (!second.ok) throw new Error('Page response ' + second.status);
      html = await second.text();
      documentPage = new DOMParser().parseFromString(html, 'text/html');
      return documentPage;
    }

    async loadNext() {
      if (this.loading || this.finished || !this.nextUrl || !this.isConnected) return;
      this.loading = true;
      this.loader.hidden = false;
      this.error.hidden = true;
      this.fallback.hidden = true;
      this.abortController = new AbortController();

      try {
        const url = new URL(this.nextUrl, window.location.href);
        const page = await this.fetchPage(url);
        const fetchedGrid = page.querySelector('#product-grid');
        if (!fetchedGrid) throw new Error('Next product grid missing');
        if (!this.isConnected) return;

        const items = Array.from(fetchedGrid.children).filter((node) => node.matches('li.grid__item'));
        if (!items.length) throw new Error('Next product batch is empty');

        const fragment = document.createDocumentFragment();
        for (const item of items) {
          // Dawn may not initialize its reveal animation for appended cards.
          item.classList.remove('scroll-trigger', 'animate--slide-in');
          item.removeAttribute('data-cascade');
          item.style.removeProperty('--animation-order');
          fragment.appendChild(item);
        }
        this.grid.appendChild(fragment);

        const nextElement = page.querySelector('collection-infinite-scroll[data-next-url]');
        this.nextUrl = nextElement?.dataset.nextUrl || '';
        this.dataset.nextUrl = this.nextUrl;
        if (this.nextUrl) {
          this.fallback.href = this.nextUrl;
        } else {
          this.finished = true;
          this.remove();
        }
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          console.warn('Collection auto-load failed:', error);
          this.error.hidden = false;
          this.fallback.hidden = false;
        }
      } finally {
        if (this.isConnected) this.loader.hidden = true;
        this.loading = false;
        // If the new row is still shorter than the viewport, load another batch.
        if (this.isConnected && !this.finished && this.error.hidden) this.onScroll();
      }
    }
  });
}
